import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Gift, MapPin, Heart, Settings, Clock, MapPin as MapPinIcon, MessageSquare, Users, HelpCircle, Eye, Map as MapIcon, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import NearbyUsersMap from "@/components/NearbyUsersMap";
import useProximityAlerts from "@/hooks/useProximityAlerts";
import StreetViewMap from "@/components/StreetViewMap";
import useNearbyUsers, { MapBounds } from "@/hooks/useNearbyUsers";
import { useLocationSharingContext } from "@/contexts/LocationSharingContext";
import { supabase } from "@/integrations/supabase/client";
import { calculateDistance, formatDistance } from "@/lib/distance";
import { formatLastSeen, isOnline } from "@/lib/time";
import { SaveUserButton } from "@/components/SaveUserButton";
import { UserSearch } from "@/components/UserSearch";
import { checkStreetViewAvailability } from "@/lib/streetViewUtils";
import { toast } from "sonner";
import useAuth, { SUPERUSER_BYPASS_EMAIL, SUPERUSER_BYPASS_ENABLED } from "@/hooks/useAuth";

const MapView = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const { users: nearbyUsers, loading, refresh } = useNearbyUsers(currentUserId, mapBounds);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const { sharing, error, privacyLevel, setPrivacyLevel, startSharing, stopSharing, checkInOnce } = useLocationSharingContext() as any;
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [currentUserLocation, setCurrentUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [radiusLimit, setRadiusLimit] = useState<number>(5);
  const hasSetInitialCenter = useRef(false);
  const [viewMode, setViewMode] = useState<'map' | 'streetview'>('map');
  const [streetViewLoading, setStreetViewLoading] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [iosSignInPromptOpen, setIosSignInPromptOpen] = useState(false);
  const wasSignedInRef = useRef<boolean>(false);
  const [iosBannerDismissed, setIosBannerDismissed] = useState(false);
  const [iosPromptDismissed, setIosPromptDismissed] = useState(false);
  const [selectedIsBlocked, setSelectedIsBlocked] = useState(false);
  const isBypassSuperuser = !!authUser?.email && SUPERUSER_BYPASS_ENABLED && authUser.email.toLowerCase() === SUPERUSER_BYPASS_EMAIL;

  const DEFAULT_MAP_CENTER = { lat: 37.7749, lng: -122.4194 };
  const BYPASS_PROFILE_STORAGE_KEY = "giftmap_superuser_profile";

  const getBypassProfile = () => {
    try {
      const raw = localStorage.getItem(BYPASS_PROFILE_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  // Proximity alerts: notify within 20m of an online user
  useProximityAlerts(
    currentUserId,
    currentUserLocation,
    nearbyUsers,
    (userId) => navigate(`/gift?user=${userId}`),
    { proximityMeters: 20, cooldownMs: 10 * 60 * 1000 }
  );

  const getCurrentUserId = async () => {
    try {
      if (isBypassSuperuser && authUser?.id) {
        setCurrentUserId(authUser.id);
        return authUser.id;
      }
      const { data } = await supabase.auth.getSession();
      const id = data?.session?.user?.id ?? null;
      setCurrentUserId(id);
      return id;
    } catch (e) {
      console.warn("Failed to get supabase session", e);
      setCurrentUserId(null);
      return null;
    }
  };

  useEffect(() => {
    if (isBypassSuperuser && authUser?.id) {
      setCurrentUserId(authUser.id);
    }
  }, [isBypassSuperuser, authUser?.id]);

  useEffect(() => {
    // Set initial center ONLY ONCE when location becomes available
    if (currentUserLocation && !hasSetInitialCenter.current) {
      setMapCenter(currentUserLocation);
      hasSetInitialCenter.current = true;
    }
  }, [currentUserLocation]);

  useEffect(() => {
    if (hasSetInitialCenter.current) return;
    if (currentUserLocation) return;
    if (nearbyUsers.length === 0) return;

    const firstWithCoords = nearbyUsers.find((u) => u.lat != null && u.lng != null);
    if (!firstWithCoords) return;

    setMapCenter({ lat: Number(firstWithCoords.lat), lng: Number(firstWithCoords.lng) });
    hasSetInitialCenter.current = true;
  }, [nearbyUsers, currentUserLocation]);

  useEffect(() => {
    if (hasSetInitialCenter.current) return;
    if (currentUserLocation) return;
    if (nearbyUsers.length > 0) return;

    setMapCenter(DEFAULT_MAP_CENTER);
    hasSetInitialCenter.current = true;
  }, [nearbyUsers.length, currentUserLocation]);

  useEffect(() => {
    // initial session read
    getCurrentUserId();

    // subscribe to auth changes
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const id = (session as any)?.user?.id ?? null;
      setCurrentUserId(id);
    });

    return () => {
      // unsubscribe
      try {
        sub?.subscription?.unsubscribe?.();
      } catch {}
    };
  }, []);

  // On sign-in, show iOS prompt (no auto-checkin)
  useEffect(() => {
    const platform = Capacitor.getPlatform?.() || 'web';
    const nowSignedIn = !!currentUserId;
    const wasSignedIn = wasSignedInRef.current;
    if (platform === 'ios' && nowSignedIn && !wasSignedIn) {
      // First transition to signed-in on iOS
      if (!sharing) setIosSignInPromptOpen(true);
    }
    wasSignedInRef.current = nowSignedIn;
  }, [currentUserId, sharing]);

  // Fetch the current user's row (lat/lng) directly and subscribe to updates for that user
  useEffect(() => {
    let mounted = true;
    let channel: any = null;

    const fetchUserAndLocation = async (id: string) => {
      try {
        const { data, error } = await supabase.from('users').select('id, name, avatar_url, lat, lng, status, location_updated_at, radius_limit_miles').eq('id', id).single();
        if (error) {
          console.warn('Failed to fetch current user row', error);
          return;
        }
        if (!mounted) return;
        
        const userRecord = {
          id: data.id,
          name: data.name,
          avatar_url: data.avatar_url,
          lat: Number(data.lat),
          lng: Number(data.lng),
          status: data.status,
          location_updated_at: data.location_updated_at,
        };
        setCurrentUser(userRecord);
        setRadiusLimit(data.radius_limit_miles || 5);

        if (data && typeof data.lat === 'number' && typeof data.lng === 'number') {
          setCurrentUserLocation({ lat: Number(data.lat), lng: Number(data.lng) });
        } else {
          setCurrentUserLocation(null);
        }
      } catch (e) {
        console.warn('Error fetching user row', e);
      }
    };

    if (currentUserId) {
      if (isBypassSuperuser) {
        const bypassProfile = getBypassProfile();
        const fallbackLoc = currentUserLocation || mapCenter || DEFAULT_MAP_CENTER;
        setCurrentUserLocation(fallbackLoc);
        setCurrentUser({
          id: currentUserId,
          name: bypassProfile?.name || "Superuser",
          avatar_url: bypassProfile?.avatar_url || `https://api.dicebear.com/7.x/personas/svg?seed=${encodeURIComponent(authUser?.email || "superuser")}`,
          lat: Number(fallbackLoc.lat),
          lng: Number(fallbackLoc.lng),
          status: bypassProfile?.status || null,
          location_updated_at: new Date().toISOString(),
        });
        return;
      }

      fetchUserAndLocation(currentUserId);

      try {
        channel = supabase
          .channel(`public:users:id=${currentUserId}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'users', filter: `id=eq.${currentUserId}` }, (payload: any) => {
            const row: any = payload.new || payload.old;
            if (!row) return;
            
            if (payload.eventType === 'DELETE') {
              setCurrentUserLocation(null);
              setCurrentUser(null);
              return;
            }

            const updatedRecord = {
              id: row.id,
              name: row.name,
              avatar_url: row.avatar_url,
              lat: Number(row.lat),
              lng: Number(row.lng),
              status: row.status,
              location_updated_at: row.location_updated_at,
            };
            setCurrentUser(updatedRecord);

            if (row.lat != null && row.lng != null) {
              setCurrentUserLocation({ lat: Number(row.lat), lng: Number(row.lng) });
            } else {
              setCurrentUserLocation(null);
            }
          })
          .subscribe();
      } catch (e) {
        console.warn('Failed to subscribe to user changes', e);
      }
    } else {
      setCurrentUserLocation(null);
      setCurrentUser(null);
    }

    return () => {
      mounted = false;
      try {
        if (channel) supabase.removeChannel(channel);
      } catch {}
    };
  }, [currentUserId, isBypassSuperuser, mapCenter]);

  // Location sharing is now managed by LocationSharingContext at the app level
  // This ensures users remain visible on the map when navigating to other pages

  // Include current user in map display but exclude from selectable list
  const radiusKm = radiusLimit * 1.60934; // Convert miles to km
  const filterByRadiusAndOnline = (u: any) => {
    // Exclude current user here; we append current user separately if available
    if (currentUserId && u.id === currentUserId) return false;
    // Must have coordinates
    if (u.lat == null || u.lng == null) return false;
    // Online-only
    if (!isOnline(u.location_updated_at)) return false;
    // If we know current location, enforce radius limit
    if (currentUserLocation) {
      const distance = calculateDistance(
        currentUserLocation.lat,
        currentUserLocation.lng,
        u.lat,
        u.lng
      );
      return distance <= radiusKm;
    }
    return true;
  };

  const allUsers = currentUserId
    ? [
        ...nearbyUsers.filter(filterByRadiusAndOnline),
        ...(currentUser && currentUser.lat != null && currentUser.lng != null ? [currentUser] : [])
      ]
    : nearbyUsers.filter(filterByRadiusAndOnline);

  // For the user list below the map, filter by radius and exclude the current user
  const selectableUsers = currentUserId 
    ? nearbyUsers.filter(u => {
        if (u.id === currentUserId) return false;
        if (!isOnline(u.location_updated_at)) return false;
        if (!currentUserLocation) return true;
        const distance = calculateDistance(
          currentUserLocation.lat,
          currentUserLocation.lng,
          u.lat,
          u.lng
        );
        return distance <= radiusKm;
      })
    : nearbyUsers.filter(u => {
        if (!isOnline(u.location_updated_at)) return false;
        if (!currentUserLocation) return true;
        const distance = calculateDistance(
          currentUserLocation.lat,
          currentUserLocation.lng,
          u.lat,
          u.lng
        );
        return distance <= radiusKm;
      });

  useEffect(() => {
    if (!selectedUser && selectableUsers.length > 0) {
      setSelectedUser(selectableUsers[0]);
    }
  }, [selectableUsers, selectedUser]);

  const handleRecenter = () => {
    if (currentUserLocation) {
      setRecenterTrigger(prev => prev + 1);
    }
  };

  // Determine if the selected user is blocked by the current user
  useEffect(() => {
    const check = async () => {
      if (!currentUserId || !selectedUser?.id) { setSelectedIsBlocked(false); return; }
      try {
        const { data, error } = await supabase
          .from('user_blocks')
          .select('blocked_id')
          .eq('blocker_id', currentUserId)
          .eq('blocked_id', selectedUser.id)
          .maybeSingle();
        if (error && (error as any).code !== 'PGRST116') throw error;
        setSelectedIsBlocked(!!data);
      } catch {
        setSelectedIsBlocked(false);
      }
    };
    check();
  }, [currentUserId, selectedUser?.id]);

  const handleStreetViewToggle = async () => {
    if (viewMode === 'streetview') {
      setViewMode('map');
      return;
    }

    if (!currentUserLocation) {
      toast.error("Location required for Street View");
      return;
    }

    setStreetViewLoading(true);
    try {
      const available = await checkStreetViewAvailability(
        currentUserLocation.lat,
        currentUserLocation.lng
      );
      
      if (available) {
        setViewMode('streetview');
      } else {
        toast.error("Street View not available at your location");
      }
    } catch (error) {
      toast.error("Failed to load Street View");
    } finally {
      setStreetViewLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-warm overflow-x-hidden">
      <div className="container mx-auto px-4 pt-safe pb-6 max-w-md md:max-w-2xl lg:max-w-4xl overflow-x-hidden">
        {/* Header - Row 1: Title and icon buttons */}
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold bg-gradient-sunset bg-clip-text text-transparent">
            Nearby Users
          </h1>
          <div className="flex items-center gap-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/help")}
              className="h-10 w-10"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings")}
              className="h-10 w-10"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Header - Row 2: All navigation in single row */}
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="border-primary/30 h-8 px-2 text-xs"
              onClick={() => navigate("/saved-users")}
            >
              <Users className="w-3.5 h-3.5 mr-1" />
              Saved
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-primary/30 h-8 px-2 text-xs"
              onClick={() => navigate("/dashboard")}
            >
              Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <select
              aria-label="Location sharing level"
              value={privacyLevel}
              onChange={(e) => setPrivacyLevel(e.target.value as any)}
              className="text-xs h-8 px-2 border border-border/50 rounded bg-background"
            >
              <option value="off">Off</option>
              <option value="coarse">Coarse</option>
              <option value="precise">Precise</option>
            </select>
            {/* iOS requires manual check-in; toggle sharing (no auto start) */}
            {(() => {
              try {
                const platform = Capacitor.getPlatform?.() || 'web';
                if (platform === 'ios') {
                  return (
                    <Button
                      size="sm"
                      className="h-8 px-3 text-xs"
                      onClick={async () => {
                        const userId = await getCurrentUserId();
                        if (!userId) { navigate("/auth"); return; }
                        if (sharing) {
                          stopSharing();
                        } else {
                          const success = await startSharing();
                          if (success) {
                            // Immediately refetch user data to sync currentUserLocation
                            try {
                              const { data } = await supabase
                                .from('users')
                                .select('id, name, avatar_url, lat, lng, status, location_updated_at, radius_limit_miles')
                                .eq('id', userId)
                                .single();
                              if (data && data.lat != null && data.lng != null) {
                                setCurrentUserLocation({ lat: Number(data.lat), lng: Number(data.lng) });
                                setCurrentUser({
                                  id: data.id,
                                  name: data.name,
                                  avatar_url: data.avatar_url,
                                  lat: Number(data.lat),
                                  lng: Number(data.lng),
                                  status: data.status,
                                  location_updated_at: data.location_updated_at,
                                });
                              }
                            } catch (e) {
                              console.warn('Failed to refetch user after check-in', e);
                            }
                          }
                        }
                      }}
                    >
                      {sharing ? 'Check Out' : 'Check In'}
                    </Button>
                  );
                }
              } catch {}
              return (
                <Button
                  size="sm"
                  className={`h-8 px-3 text-xs ${sharing ? "bg-primary text-primary-foreground" : ""}`}
                  onClick={async () => {
                    const userId = await getCurrentUserId();
                    if (!userId) { navigate("/auth"); return; }
                    if (sharing) stopSharing(); else startSharing();
                  }}
                >
                  {sharing ? "Stop" : "Share"}
                </Button>
              );
            })()}
          </div>
        </div>

        {error ? (
          <div className="text-sm text-red-600 mb-4">Location sharing error: {error}</div>
        ) : null}

        {/* iOS Visibility Banner / Prompt */}
        {(() => {
          try {
            const platform = Capacitor.getPlatform?.() || 'web';
            if (platform === 'ios') {
              if (sharing && !iosBannerDismissed) {
                return (
                  <Card className="mb-3 p-2 border-border/50 bg-emerald-50">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-emerald-900">You’re visible to nearby users.</div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setIosBannerDismissed(true)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </Card>
                );
              }
              if (!iosPromptDismissed) {
                return (
                <Card className="mb-3 p-2 border-border/50 bg-amber-50">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-amber-900">
                      To appear to nearby users, tap "Check In" above. {error?.toLowerCase().includes('permission') ? 'Enable location in iOS Settings if previously denied.' : ''}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setIosPromptDismissed(true)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </Card>
                );
              }
            }
          } catch {}
          return null;
        })()}

        {/* iOS Sign-in Modal Prompt */}
        <AlertDialog open={iosSignInPromptOpen} onOpenChange={setIosSignInPromptOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Share your location</AlertDialogTitle>
              <AlertDialogDescription>
                To appear on the map, tap "Check In". You control when to "Check Out" and stop sharing.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setIosSignInPromptOpen(false)}>OK</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Search Section */}
        <Card className="mb-4 p-4 shadow-warm border-border/50">
          <UserSearch 
            excludeUserId={currentUserId || undefined}
            onUserSelect={(userId) => navigate(`/gift?user=${userId}`)}
          />
        </Card>

        {/* Map/Street View Toggle */}
        <div className="flex gap-2 mb-3">
          <Button
            variant={viewMode === 'map' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('map')}
            className="flex-1"
          >
            <MapIcon className="w-4 h-4 mr-2" />
            Map
          </Button>
          <Button
            variant={viewMode === 'streetview' ? 'default' : 'outline'}
            size="sm"
            onClick={handleStreetViewToggle}
            disabled={streetViewLoading || !currentUserLocation}
            className="flex-1"
          >
            <Eye className="w-4 h-4 mr-2" />
            {streetViewLoading ? 'Loading...' : 'Street View'}
          </Button>
        </div>

        {/* Google Map or Street View */}
        <Card className="mb-6 overflow-hidden shadow-warm border-border/50">
          <div className="h-[400px] md:h-[500px] lg:h-[600px] relative">
            {viewMode === 'streetview' && currentUserLocation ? (
              <StreetViewMap
                users={allUsers}
                currentUserLocation={currentUserLocation}
                currentUserId={currentUserId}
                onSendGift={(userId) => navigate(`/gift?user=${userId}`)}
                onClose={() => setViewMode('map')}
              />
            ) : mapCenter ? (
              <NearbyUsersMap
                users={allUsers}
                selectedUser={selectedUser}
                onUserSelect={setSelectedUser}
                currentUserId={currentUserId}
                defaultCenter={mapCenter}
                recenterTrigger={recenterTrigger}
                recenterTarget={currentUserLocation}
                currentUserLocation={currentUserLocation}
                onSendGift={(userId) => navigate(`/gift?user=${userId}`)}
                onBoundsChanged={(b) => setMapBounds(b)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                Loading map...
              </div>
            )}
            {viewMode === 'map' && currentUserLocation && (
              <Button
                onClick={handleRecenter}
                className="absolute top-4 right-4 shadow-lg z-10"
                size="sm"
              >
                Recenter
              </Button>
            )}
          </div>
        </Card>

        {/* Selected User Card */}
        {selectedUser ? (
          <Card className="p-6 shadow-warm border-border/50 space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 border-2 border-white shadow-soft">
                <AvatarImage src={selectedUser.avatar_url || undefined} />
                <AvatarFallback>
                  {selectedUser.name ? selectedUser.name[0] : '?'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl font-semibold">{selectedUser.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Nearby
                </p>
                {selectedUser.status && (
                  <p className="text-sm text-accent flex items-center gap-1 mt-1">
                    <MessageSquare className="w-3 h-3" />
                    {selectedUser.status}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                {selectedUser.name?.toLowerCase().startsWith('ambassador') && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-primary/50"
                    onClick={() => navigate(`/prize-draw?user=${selectedUser.id}`)}
                  >
                    <Ticket className="w-4 h-4 mr-1" />
                    Draw
                  </Button>
                )}
                <Button
                  size="sm"
                  className="flex-1 bg-primary hover:bg-primary/90 shadow-soft"
                  onClick={() => navigate(`/gift?user=${selectedUser.id}`)}
                >
                  <Gift className="w-4 h-4 mr-1" />
                  Gift
                </Button>
                {/* Server-backed block/unblock */}
                {selectedIsBlocked ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-destructive/50"
                    onClick={async () => {
                      if (!currentUserId) return;
                      try {
                        const { error } = await supabase
                          .from('user_blocks')
                          .delete()
                          .eq('blocker_id', currentUserId)
                          .eq('blocked_id', selectedUser.id);
                        if (error) throw error;
                        setSelectedIsBlocked(false);
                        await refresh();
                      } catch (e) {
                        console.error('Unblock failed', e);
                      }
                    }}
                  >
                    Unblock
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-destructive/50"
                    onClick={async () => {
                      if (!currentUserId) return;
                      try {
                        const { error } = await supabase
                          .from('user_blocks')
                          .insert({ blocker_id: currentUserId, blocked_id: selectedUser.id });
                        if (error) throw error;
                        setSelectedIsBlocked(true);
                        await refresh();
                      } catch (e) {
                        console.error('Block failed', e);
                      }
                    }}
                  >
                    Block
                  </Button>
                )}
                <SaveUserButton userId={selectedUser.id} userName={selectedUser.name} size="sm" />
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-6 shadow-warm border-border/50 text-center text-muted-foreground">
            No user selected
          </Card>
        )}

        {/* User List */}
        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="text-center text-sm text-muted-foreground">Loading nearby users…</div>
          ) : selectableUsers.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground">No nearby users found.</div>
          ) : (
            selectableUsers.map((user) => (
              <HoverCard key={user.id} openDelay={200} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => setSelectedUser(user)}
                    className={`w-full text-left transition-all ${
                      selectedUser && selectedUser.id === user.id ? 'scale-105' : 'scale-100'
                    }`}
                  >
                    <Card className={`p-4 shadow-soft border ${
                      selectedUser && selectedUser.id === user.id ? 'border-primary bg-primary/5' : 'border-border/50'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={user.avatar_url || undefined} />
                            <AvatarFallback style={{ backgroundColor: '#ddd' }}>
                              {user.name ? user.name[0] : '?'}
                            </AvatarFallback>
                          </Avatar>
                          {isOnline(user.location_updated_at) && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {currentUserLocation 
                              ? formatDistance(calculateDistance(
                                  currentUserLocation.lat,
                                  currentUserLocation.lng,
                                  user.lat,
                                  user.lng
                                ))
                              : 'Nearby'
                            }
                          </div>
                        </div>
                        <SaveUserButton userId={user.id} userName={user.name} />
                      </div>
                    </Card>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="left" className="w-64">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        isOnline(user.location_updated_at) ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                      <span className="text-sm font-medium">
                        {isOnline(user.location_updated_at) ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    
                    <div className="flex items-start gap-2 text-sm">
                      <MapPinIcon className="w-4 h-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">Distance</div>
                        <div className="text-muted-foreground">
                          {currentUserLocation 
                            ? formatDistance(calculateDistance(
                                currentUserLocation.lat,
                                currentUserLocation.lng,
                                user.lat,
                                user.lng
                              ))
                            : 'Unknown'
                          }
                        </div>
                      </div>
                    </div>
                    
                      <div className="flex items-start gap-2 text-sm">
                        <Clock className="w-4 h-4 mt-0.5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">Last seen</div>
                          <div className="text-muted-foreground">
                            {formatLastSeen(user.location_updated_at)}
                          </div>
                        </div>
                      </div>

                      {user.status && (
                        <div className="flex items-start gap-2 text-sm">
                          <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground" />
                          <div>
                            <div className="font-medium">Status</div>
                            <div className="text-muted-foreground italic">
                              "{user.status}"
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </HoverCardContent>
                </HoverCard>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MapView;